import React from 'react';
import styles from './ArticleHeroImageBackground.module.scss';
import { RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import ArticleAuthors from './ArticleAuthors';
import ArticleContributors from './ArticleContributors';
import { NextImage } from '@sitecore-jss/sitecore-jss-nextjs';
import { ArticleHeroProps } from './ArticleHeroTypes';
import Badge from '@/components/includes/Badge/Badge';

const ArticleHeroImageBackground: React.FC<ArticleHeroProps> = ({
  article,
  anchorId,
  tagValues,
}) => {
  if (!article) return null;

  const authorsLength = article.articleAuthor?.targetItems.length;

  return (
    <section
      id={anchorId}
      className="background-white"
      itemScope
      itemType="https://schema.org/NewsArticle"
    >
      <div className={styles['section-header-wrapper']}>
        <NextImage
          media={article.articleImage}
          className={styles['section-background']}
          lazy={false}
          alt={article.articleImage?.alt ?? 'Article hero background image'}
          itemProp="image"
          sizes="100vw"
        />

        <div className="mt-auto pt-10">
          <div className="container">
            <div className="row">
              <div className="col-10 col-md-9 col-lg-7">
                <div className={styles['section-header']}>
                  {(article.contentGroup || article.articleCategory) && (
                    <div className="mb-2 d-flex flex-column flex-md-row align-items-md-center gap-1 gap-md-4">
                      {tagValues?.firstTag && (
                        <div className="h5 text-nowrap">{tagValues.firstTag}</div>
                      )}
                      {tagValues?.secondTag && <Badge>{tagValues.secondTag}</Badge>}
                    </div>
                  )}

                  <Text tag="h2" itemProp="headline" field={article.articleTitle} className="h2" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="py-4 py-lg-8">
          <div className="row">
            <div className="col-12 col-md-9">
              <RichText
                tag="p"
                field={article.articleStrapline}
                className="subheader-1 text-grey"
              />
            </div>
          </div>

          {(authorsLength || article.articleContributor.targetItems.length > 0) && (
            <div className="row mt-4 mt-md-5 mt-lg-6">
              <div className="col-12 col-md-9 col-lg-12">
                <div className="row mt-n3">
                  {authorsLength > 0 && (
                    <div
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                      className="col-12 col-lg-6 mt-3"
                    >
                      <ArticleAuthors authors={article.articleAuthor.targetItems} />
                    </div>
                  )}
                  {article.articleContributor && (
                    <ArticleContributors
                      contributors={article.articleContributor.targetItems}
                      className="col-12 col-lg-6 mt-3"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ArticleHeroImageBackground;
